<script>
    import {_, locale, number} from 'svelte-i18n';
    import Fa from 'svelte-fa';
    import {faCircleCheck, faXmark} from '@fortawesome/free-solid-svg-icons';
    import {getAnalytics, logEvent} from 'firebase/analytics';
    import {onDestroy, onMount} from 'svelte';
    import {buy_pack_route, game_name} from '../constants';

    import {
        getStorePacks,
        giftFriend,
        selectedCurrency,
        storePacks,
        storeSection,
        subscriptions,
        user,
        endExpiredSession,
        isSessionExpired,
    } from '../stores';
    import {logMetric} from '../metrics';

    // `embedded` = rendered inside the mobile pack detail sheet (which already
    // provides its own close button), so hide this component's own close button.
    let { storePack, packDetails, payments_modal_hide, embedded = false } = $props();

    // Log event when the cart is viewed
    const analytics = getAnalytics();
    let params = $derived({
        currency: storePack.paymentHub.price.currency,
        value: storePack.paymentHub.price.amount / 100,
        items: [
            {
                name: packDetails.title,
                id: storePack.internalId,
                price: storePack.paymentHub.price.amount / 100,
            },
        ],
    });

    let succeded = $state(false);
    let error = $state(null);
    let processing = $state(false);
    let checkoutPage;

    let showIntentWarning = $state(false);
    let agree_tos = $state(true);
    let initError = $state(false);
    // init-payment refused the pack (503), as opposed to failing.
    let packUnavailable = $state(false);
    let initProcessing = $state(false);

    function handleWindowFocus() {
        if (processing) {
            setTimeout(() => {
                if ($giftFriend.startPurchase) {
                    $giftFriend.startPurchase = false;
                }
                refreshPacks();
                payments_modal_hide();
                processing = false;
            }, 1500);
        }
    }

    onMount(async () => {
        succeded = false;

        logMetric({buy_begin_checkout: params});
        // create payment intent server side
        logEvent(analytics, 'begin_checkout', params);

        window.addEventListener('focus', handleWindowFocus);
    });

    onDestroy(() => {
        window.removeEventListener('focus', handleWindowFocus);

        if (!succeded) {
            logMetric({buy_cancel_checkout: params});
            // cancel payment intent server side
            logEvent(analytics, 'cancel_checkout', params);
        }
    });

    $effect(() => {
        if (succeded) {
            logMetric({buy_purchase_succeded: params});
            logEvent(analytics, 'purchase', params);
        }
    });

    $effect(() => {
        if (error) {
            logMetric({buy_purchase_error: error});
            logEvent(analytics, 'purchase_error', params);
        }
    });

    function refreshPacks() {
        setTimeout(async () => {
            storePacks.set([]);
            subscriptions.set([]);
            await getStorePacks(false, { fetchStaticData: false });
            storeSection.set($storeSection);
        }, 1200);
    }

    async function createPaymentIntent() {
        if (initProcessing) return;
        initProcessing = true;

        try {
            let params;

            if ($giftFriend.startPurchase) {
                params = {
                    packId: storePack.internalId,
                    currencyCode: storePack.paymentHub.price.currency,
                    price: storePack.paymentHub.price.amount,
                    paymentsProcessor: 'PaymentHub',
                    email: $user.user.email,
                    message: $giftFriend.message,
                    description: $_(packDetails.title),
                    locale: $locale
                };
            } else {
                params = {
                    packId: storePack.internalId,
                    currencyCode: storePack.paymentHub.price.currency,
                    price: storePack.paymentHub.price.amount,
                    paymentsProcessor: 'PaymentHub',
                    email: $user.user.email,
                    description: $_(packDetails.title),
                    locale: $locale
                };
            }

            const response = await fetch(buy_pack_route, {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(params),
            });

            // web-shop's 403 is the LoggedInGuard: the session is gone, so swap the shop for <Login/>
            // as every other API caller does (Rollbar #2519) instead of reporting it as critical
            // (#1349). Cloudflare's 403 is not, and stays a reported failure below.
            if (isSessionExpired(response)) {
                endExpiredSession(buy_pack_route);
                return;
            }

            // 503: the server cannot sell this pack right now (PaymentHub has no quote for it, or
            // the session lost its PaymentHub customer, e.g. another tab's refresh during an
            // outage). Expected, so info; refresh so the shop stops offering it. Not with force,
            // which makes the backend skip the webshop entry requirement.
            if (response.status === 503) {
                initError = true;
                packUnavailable = true;
                Rollbar.info('Payment intent refused: pack not available right now');
                getStorePacks(false, { fetchStaticData: false });
                return;
            }

            if (!response.ok) {
                initError = true;
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const res = await response.json();

            if (res.showIntentWarning !== undefined) {
                showIntentWarning = res.showIntentWarning;
            }

            if (res.checkoutPage) {
                checkoutPage = res.checkoutPage;
            }

            setTimeout(() => {
                if (res.checkoutPage && !res.showIntentWarning) {
                    window.open(res.checkoutPage, '_blank');
                    processing = true;
                }
            }, 50);
        } catch (error) {
            if (error instanceof TypeError) {
                Rollbar.info('Payment intent network failure: ' + error);
            } else {
                Rollbar.critical('Error creating payment intent: ' + error);
            }
        } finally {
            initProcessing = false;
        }
    }

    async function continueToCheckout() {
        if (checkoutPage) {
            window.open(checkoutPage, '_blank');
            processing = true;
            showIntentWarning = false;
        }
    }


</script>

<div class="card payments">
    {#if !embedded}
        <!-- svelte-ignore a11y_invalid_attribute -->
        <a class="close-button" href="javascript:" onclick={payments_modal_hide}>
            <Fa fw icon={faXmark} primaryColor="#240c05" size="2x"/>
        </a>
    {/if}

    {#if succeded}
        <div class="success" use:refreshPacks>
            <h1>{$_('PurchaseCompletedSubTitle')}</h1>
            <Fa icon={faCircleCheck} fw size="5x" primaryColor="green"/>
            <h4>
                {$_('PurchaseCompletedMessage')}
            </h4>
            <p>
                {$_('PackPurchasedReportTitle', {
                    values: {
                        PACK_TITLE: $_(packDetails.title),
                    },
                })}
            </p>
            <!-- svelte-ignore a11y_invalid_attribute -->
            <a href="javascript:" onclick={payments_modal_hide}>{$_('Close')}</a>
        </div>
    {:else}
        {#if showIntentWarning}
            <div class="continue-payment show-intent-warning">
                <h1>Important Notice!</h1>
                <div>{@html $_('GameTokenWarningMessageMultipleTransactions').replace(/\n/g, "<br/><br/>")}</div>
                <div class="buttons">
                    <button
                            class="button primary cancel light-orange"
                            onclick={payments_modal_hide}
                    >
                        {$_('Cancel')}
                    </button>
                    <button
                            class="button primary continue orange"
                            onclick={continueToCheckout}
                    >
                        {$_('Continue')}
                    </button>
                </div>
            </div>
        {:else if processing}
            <div class="continue-payment">
                <img src="/images/sf_erpk_logo.png" alt="Stillfront & eRepublik logo"/>
                <div class="loader-spinner"></div>
                <button
                        class="button primary buy-button blue retry-button"
                        onclick={continueToCheckout}
                >
                    {$_('ClickToRetry')}
                </button>
                <b>
                    Awaiting transaction completion ...
                </b>
            </div>
        {:else}
            <div class="continue-payment">
                <img src="/images/sf_erpk_logo.png" alt="Stillfront & eRepublik logo"/>
                {#if !initError}
                    <button
                            class="button primary buy-button blue"
                            class:loading={initProcessing}
                            onclick={createPaymentIntent}
                            disabled={!agree_tos || initProcessing}
                    >
                        {$_('ContinuePayment')}
                    </button>
                {:else if packUnavailable}
                    <p style="margin: 25px 0;" class="animate__animated animate__rubberBand">This pack can't be
                        bought right now, please try again later.</p>
                {:else}
                    <p style="margin: 25px 0;" class="animate__animated animate__rubberBand">There's been an error,
                        please try again later.</p>
                {/if}
                <label class="tos">
                    <input type="checkbox" bind:checked={agree_tos}>
                    <span>I have read and agree to the <a href="/terms-of-service" target="_blank">Terms and Conditions</a></span>
                </label>
            </div>
        {/if}
    {/if}
</div>

<style lang="scss">
  @use '../styles/vars.scss' as *;

  :global {
    .checkout {
      &.modal {
        min-width: 450px !important;
      }

      @media (max-width: 700px) {
        &.modal {
          min-width: 350px !important;
        }
      }
    }

    .payments {
      align-items: center;
      flex-direction: column;
      flex-wrap: nowrap;
      justify-content: center;
      text-align: center;
      overflow: hidden;
      // Dark-brown text for the whole component. On desktop `.modal` sets this,
      // but the embedded mobile sheet has no `.modal`, so text like "Awaiting
      // transaction completion…" would inherit white — set it here for both.
      color: $bg-color;
      text-shadow: 0 1px 0 rgba(255, 255, 255, 0.4);
      transition: all 0.2s ease-in;
      position: relative;

      .close-button {
        position: absolute;
        top: 0;
        right: 0;
        padding: 7px 2px;
      }

      .wrapper {
        margin: 20px auto;
      }

      .buy-button {
        margin: 5px 0;
        padding: 1.7rem 2.5rem !important;
        white-space: nowrap;
        width: 100%;
      }

      .retry-button {
        margin: 5px auto !important;
        // "Click here if the payment window did not open" is far longer than the other
        // CTA labels, and .buy-button's 2.5rem side padding left under 210px of inner
        // width for it at 360-390px, so the label overflowed the button. Shrink the text,
        // reclaim the side padding, and take the full container width.
        //
        // NB: chota sets `html { font-size: 62.5% }`, so 1rem is 10px here, not 16px.
        // font-size needs !important because the global `.button.primary` rule sets 18px
        // at equal specificity and lands later in the bundle — same reason the margin and
        // padding above are already forced.
        width: 100%;
        padding: 1.5rem 1rem !important;
        font-size: 1.3rem !important;
        // .buy-button forces nowrap; allow wrapping here so that if this label is ever
        // translated (all ten locales currently ship the English string) it degrades to
        // a second line instead of overflowing again.
        white-space: normal;
      }

      &.card {
        padding: 0;
      }

      .payment-methods {
        display: flex;
        background-color: rgba(255, 255, 255, 0.5);
        flex-direction: column;
        padding: 1rem 2rem;
        color: #111;
        border-top: 1px solid white;
        border-bottom: 10px solid green;
        border-radius: 0 0 4px 4px;

        &.error {
          border-bottom: 10px solid $color-error;
        }
      }

      .price {
        border-top: 2px solid #532011;
        display: flex;
        flex-direction: column;
        padding: 1rem 2rem;

        & > div {
          display: flex;
          flex-direction: row;
          align-items: flex-start;
          justify-content: space-between;

          &.prices {
            border-top: 1px dotted rgb(0 0 0 / 36%);
            align-items: flex-end;
          }
        }
      }

      .total {
        border-top: 2px solid #532011;
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 1.2em;
        padding: 1rem 2rem;
        font-weight: 700;
      }

      .success {
        padding: 1rem 2rem;

        h1 {
          border-bottom: 2px solid #532011;
          margin-bottom: 15px;
        }

        a {
          color: $bg-color;
          display: block;
          padding: 25px 0;
          font-weight: bold;
        }

        p {
          background-color: rgba(255, 255, 255, 0.497);
          padding: 5px 10px;
          font-weight: 700;
          border-radius: 10px;
          color: var(--color-success);
          border: 1px solid rgba(0, 0, 0, 0.16);
          background-color: white;
          background-image: linear-gradient(
                          180deg,
                          rgba(0, 0, 0, 0.02) 0%,
                          rgba(255, 255, 255, 0.02) 50%
          );
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        }
      }

      h1 {
        font-weight: 700;
        font-family: var(--font-family-sans-condensed);
        padding: 1rem 2rem;
        line-height: 1.2em;
        margin-bottom: 0;
        margin-top: 0;
      }

      h4 {
        font-family: var(--font-family-sans-condensed);
        max-width: 400px;
        font-size: 16px;
        color: #8c7138;
      }

      .user_name {
        position: relative;
        top: -10px;
      }
    }

    .continue-payment {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      padding: 40px 40px 30px 40px;

      @media (max-width: 700px) {
        padding: 26px 20px 22px;
      }

      &.show-intent-warning {
        padding: 20px 40px 30px 40px;
      }

      button {
        width: 80%;
        margin: 30px auto !important;
      }

      b {
        font-size: 15px;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 3px;
      }

      // Terms & conditions consent — keep the sentence as one wrapping block next
      // to the checkbox (the old flex row split "…agree to the" and the link into
      // two squished columns on narrow phones).
      .tos {
        font-size: 15px;
        display: flex;
        align-items: center; // centre the checkbox against the two lines
        justify-content: center;
        gap: 8px;
        margin: 0 auto;
        text-align: left;
        line-height: 1.3;
        cursor: pointer;
        color: $bg-color; // proper dark-brown label colour (matches the desktop modal)

        // Cap the width on phones so the sentence wraps to two even lines.
        @media (max-width: 700px) {
          max-width: 210px;
        }

        input {
          flex: 0 0 auto;
          width: 16px;
          height: 16px;
          margin: 0;
        }

        span {
          text-wrap: balance; // even out the two wrapped lines
        }

        a {
          color: $rareMainColor;
        }
      }

      h1 {
        font-size: 1.6em;
        margin-bottom: 10px;
      }

      .buttons {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 30px;
        margin-top: 30px;

        button {
          margin: 0 !important;
        }
      }
    }
  }

  .loader-spinner {
    width: 50px;
    height: 50px;
    border: 4px solid rgba(0, 0, 0, 0.1);
    border-left-color: #000;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 10px 0;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

</style>
